import type { FeatureModule, FeatureState } from '../types';

interface TTSOptions {
  speed: number;
}

const DEFAULT_OPTIONS: TTSOptions = {
  speed: 1.0,
};

export default function createTTSModule(): FeatureModule {
  let enabled = false;
  let options: TTSOptions = { ...DEFAULT_OPTIONS };
  let controlBar: HTMLDivElement | null = null;
  let highlightedEl: HTMLElement | null = null;
  let isPlaying = false;
  let isPaused = false;
  let clickToReadActive = true;
  let currentUtterances: SpeechSynthesisUtterance[] = [];
  let currentUtteranceIndex = 0;
  let selectedVoice: SpeechSynthesisVoice | null = null;
  let detectedLang = 'en';

  const STORAGE_KEY = 'accessify-tts';
  const CONTROL_BAR_ID = 'accessify-tts-controls';
  const STYLE_ID = 'accessify-tts-styles';
  const HIGHLIGHT_CLASS = 'accessify-tts-highlight';

  // ---------------------------------------------------------------------------
  // Language detection
  // ---------------------------------------------------------------------------

  function detectPageLanguage(): string {
    const htmlLang = document.documentElement.lang;
    if (htmlLang) {
      return htmlLang.toLowerCase();
    }
    // Fallback: check <meta> content-language
    const meta = document.querySelector<HTMLMetaElement>(
      'meta[http-equiv="content-language"]'
    );
    if (meta?.content) {
      return meta.content.toLowerCase();
    }
    return 'en';
  }

  // ---------------------------------------------------------------------------
  // Voice selection
  // ---------------------------------------------------------------------------

  function selectBestVoice(lang: string): SpeechSynthesisVoice | null {
    const voices = speechSynthesis.getVoices();
    if (voices.length === 0) return null;

    const langBase = lang.split('-')[0]; // e.g. 'de' from 'de-DE'

    // For German, strongly prioritize native German voices
    if (langBase === 'de') {
      // Prefer high-quality (non-local) de-DE voices first
      const deDENetwork = voices.find(
        (v) => v.lang.toLowerCase().startsWith('de-de') && v.localService === false
      );
      if (deDENetwork) return deDENetwork;

      // Then any de-DE voice
      const deDE = voices.find(
        (v) => v.lang.toLowerCase().startsWith('de-de')
      );
      if (deDE) return deDE;

      // Then any German voice
      const deAny = voices.find((v) =>
        v.lang.toLowerCase().startsWith('de')
      );
      if (deAny) return deAny;
    }

    // Exact match (e.g. 'en-US' matches 'en-US')
    const exact = voices.find(
      (v) => v.lang.toLowerCase() === lang
    );
    if (exact) return exact;

    // Base language match (e.g. 'en' matches 'en-US', 'en-GB')
    const baseMatch = voices.find(
      (v) => v.lang.toLowerCase().startsWith(langBase)
    );
    if (baseMatch) return baseMatch;

    // Ultimate fallback: first available voice
    return voices[0] || null;
  }

  function refreshVoice() {
    selectedVoice = selectBestVoice(detectedLang);
  }

  /** Wait for voices to be available (Chrome loads them async). */
  function waitForVoices(timeoutMs = 3000): Promise<void> {
    return new Promise((resolve) => {
      const voices = speechSynthesis.getVoices();
      if (voices.length > 0) { resolve(); return; }
      let resolved = false;
      const handler = () => {
        if (resolved) return;
        resolved = true;
        resolve();
      };
      speechSynthesis.addEventListener('voiceschanged', handler, { once: true });
      setTimeout(handler, timeoutMs); // don't block forever
    });
  }

  // ---------------------------------------------------------------------------
  // Sentence splitting (avoids Chrome's ~15s synthesis cutoff)
  // ---------------------------------------------------------------------------

  function splitIntoSentences(text: string): string[] {
    // Split on sentence-ending punctuation followed by whitespace or end
    const raw = text.match(/[^.!?]*[.!?]+[\s]?|[^.!?]+$/g);
    if (!raw) return [text];

    const sentences: string[] = [];
    let buffer = '';

    for (const segment of raw) {
      const trimmed = segment.trim();
      if (!trimmed) continue;

      buffer += (buffer ? ' ' : '') + trimmed;

      // Flush buffer if it's reasonably long (> 80 chars) or is a complete sentence
      if (buffer.length > 80 || /[.!?]$/.test(buffer)) {
        sentences.push(buffer);
        buffer = '';
      }
    }

    if (buffer.trim()) {
      sentences.push(buffer.trim());
    }

    return sentences.length > 0 ? sentences : [text];
  }

  // ---------------------------------------------------------------------------
  // Speech engine
  // ---------------------------------------------------------------------------

  function stopSpeech() {
    speechSynthesis.cancel();
    isPlaying = false;
    isPaused = false;
    currentUtterances = [];
    currentUtteranceIndex = 0;
    removeHighlight();
    updateControlBarState();
  }

  function speakText(text: string, sourceEl?: HTMLElement) {
    stopSpeech();

    if (!text.trim()) return;

    if (sourceEl) {
      applyHighlight(sourceEl);
    }

    const sentences = splitIntoSentences(text).slice(0, 50); // cap at 50 sentences
    currentUtterances = sentences.map((sentence) => {
      const utterance = new SpeechSynthesisUtterance(sentence);
      utterance.rate = options.speed;
      utterance.lang = detectedLang;
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      return utterance;
    });

    currentUtteranceIndex = 0;
    isPlaying = true;
    isPaused = false;
    updateControlBarState();

    speakNextUtterance();
  }

  function speakNextUtterance() {
    if (currentUtteranceIndex >= currentUtterances.length) {
      isPlaying = false;
      isPaused = false;
      removeHighlight();
      updateControlBarState();
      return;
    }

    const utterance = currentUtterances[currentUtteranceIndex];

    // Safety timeout: Chrome can stall on long utterances (>15s).
    // If onend/onerror don't fire within 20s, force-advance.
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let settled = false;

    function settle() {
      if (settled) return;
      settled = true;
      if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }
    }

    timeoutId = setTimeout(() => {
      if (settled) return;
      settle();
      speechSynthesis.cancel();
      currentUtteranceIndex++;
      if (currentUtteranceIndex < currentUtterances.length && isPlaying) {
        setTimeout(() => speakNextUtterance(), 100);
      } else {
        isPlaying = false;
        isPaused = false;
        removeHighlight();
        updateControlBarState();
      }
    }, 20_000);

    utterance.onend = () => {
      settle();
      currentUtteranceIndex++;
      if (currentUtteranceIndex < currentUtterances.length && isPlaying) {
        setTimeout(() => speakNextUtterance(), 50);
      } else {
        isPlaying = false;
        isPaused = false;
        removeHighlight();
        updateControlBarState();
      }
    };

    utterance.onerror = (e) => {
      settle();
      if (e.error !== 'interrupted' && e.error !== 'canceled') {
        console.warn('[Accessify TTS] Speech error:', e.error);
        // Skip to next utterance on real errors instead of stopping entirely
        currentUtteranceIndex++;
        if (currentUtteranceIndex < currentUtterances.length && isPlaying) {
          setTimeout(() => speakNextUtterance(), 100);
          return;
        }
      }
      isPlaying = false;
      isPaused = false;
      removeHighlight();
      updateControlBarState();
    };

    speechSynthesis.speak(utterance);
  }

  function togglePause() {
    if (!isPlaying) return;

    if (isPaused) {
      speechSynthesis.resume();
      isPaused = false;
    } else {
      speechSynthesis.pause();
      isPaused = true;
    }
    updateControlBarState();
  }

  // ---------------------------------------------------------------------------
  // Highlight management
  // ---------------------------------------------------------------------------

  function applyHighlight(el: HTMLElement) {
    removeHighlight();
    el.classList.add(HIGHLIGHT_CLASS);
    highlightedEl = el;
  }

  function removeHighlight() {
    if (highlightedEl) {
      highlightedEl.classList.remove(HIGHLIGHT_CLASS);
      highlightedEl = null;
    }
    // Safety: remove from any stale elements
    document
      .querySelectorAll(`.${HIGHLIGHT_CLASS}`)
      .forEach((el) => el.classList.remove(HIGHLIGHT_CLASS));
  }

  // ---------------------------------------------------------------------------
  // Click-to-read handler
  // ---------------------------------------------------------------------------

  function handleClick(e: MouseEvent) {
    if (!enabled || !clickToReadActive) return;

    const target = e.target as HTMLElement;
    if (!target) return;

    // Ignore clicks on the control bar itself
    if (controlBar?.contains(target)) return;

    // Ignore clicks inside the Accessify widget (Shadow DOM host)
    const widgetRoot = document.getElementById('accessify-root');
    if (widgetRoot && (widgetRoot === target || widgetRoot.contains(target))) return;

    // Don't block clicks on interactive elements (links, buttons, inputs)
    const tag = target.tagName;
    if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA' ||
        target.closest('a, button, input, select, textarea, [role="button"]')) {
      return;
    }

    e.preventDefault();

    // Walk up to find the most meaningful text container
    const textEl = findTextElement(target);
    if (!textEl) return;

    const text = extractTextContent(textEl);
    if (text.trim()) {
      speakText(text, textEl);
    }
  }

  function findTextElement(el: HTMLElement): HTMLElement | null {
    // If the element itself has substantial text, use it
    const directText = extractTextContent(el);
    if (directText.trim().length > 0) return el;

    // Walk up to find a parent with text
    let current: HTMLElement | null = el;
    while (current && current !== document.body) {
      const text = extractTextContent(current);
      if (text.trim().length > 0) return current;
      current = current.parentElement;
    }
    return null;
  }

  function extractTextContent(el: HTMLElement): string {
    // Use innerText rather than textContent to get rendered text with spacing
    return (el.innerText || el.textContent || '').trim();
  }

  // ---------------------------------------------------------------------------
  // Control bar UI
  // ---------------------------------------------------------------------------

  // Beschriftungen folgen der erkannten Seitensprache, damit auf einer deutschen
  // Seite nicht plötzlich eine englische Vorlese-Leiste erscheint.
  function ttsLabels() {
    const de = detectedLang.toLowerCase().startsWith('de');
    return de
      ? {
          toolbar: 'Vorlesen-Steuerung',
          playPause: 'Abspielen / Pause',
          pause: 'Pause',
          resume: 'Fortsetzen',
          stop: 'Stopp',
          speed: 'Tempo',
          hintTouch: 'Auf einen Text tippen, um ihn vorlesen zu lassen',
          hintClick: 'Auf einen Text klicken, um ihn vorlesen zu lassen',
          speaking: 'Liest vor …',
          paused: 'Pausiert',
          srNote: 'Für vollen Screenreader-Support NVDA, JAWS oder VoiceOver verwenden',
        }
      : {
          toolbar: 'Text-to-Speech controls',
          playPause: 'Play / Pause',
          pause: 'Pause',
          resume: 'Resume',
          stop: 'Stop',
          speed: 'Speed',
          hintTouch: 'Tap any text to read it aloud',
          hintClick: 'Click any text to read it aloud',
          speaking: 'Speaking...',
          paused: 'Paused',
          srNote: 'For full screen reader support, use NVDA, JAWS, or VoiceOver',
        };
  }

  function createControlBar(): HTMLDivElement {
    const lbl = ttsLabels();
    const bar = document.createElement('div');
    bar.id = CONTROL_BAR_ID;
    bar.setAttribute('role', 'toolbar');
    bar.setAttribute('aria-label', lbl.toolbar);

    Object.assign(bar.style, {
      position: 'fixed',
      top: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '2147483646',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '6px',
      padding: '6px 14px',
      maxWidth: '100vw',
      boxSizing: 'border-box',
      background: '#1a1a2e',
      color: '#f0f0f0',
      borderRadius: '0 0 10px 10px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '13px',
      lineHeight: '1',
      userSelect: 'none',
      transition: 'opacity 0.2s ease',
    });

    // Play/Pause button
    const playPauseBtn = document.createElement('button');
    playPauseBtn.className = 'accessify-tts-btn accessify-tts-play';
    playPauseBtn.setAttribute('aria-label', lbl.playPause);
    playPauseBtn.title = lbl.playPause;
    playPauseBtn.innerHTML = iconPlay();
    playPauseBtn.addEventListener('click', () => {
      if (isPlaying) {
        togglePause();
      }
      // If nothing is playing, the user should click an element to start
    });

    // Stop button
    const stopBtn = document.createElement('button');
    stopBtn.className = 'accessify-tts-btn accessify-tts-stop';
    stopBtn.setAttribute('aria-label', lbl.stop);
    stopBtn.title = lbl.stop;
    stopBtn.innerHTML = iconStop();
    stopBtn.addEventListener('click', () => {
      stopSpeech();
    });

    // Speed control
    const speedContainer = document.createElement('div');
    Object.assign(speedContainer.style, {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      marginLeft: '4px',
    });

    const speedLabel = document.createElement('label');
    speedLabel.textContent = lbl.speed;
    speedLabel.setAttribute('for', 'accessify-tts-speed');
    Object.assign(speedLabel.style, {
      fontSize: '11px',
      opacity: '0.8',
      whiteSpace: 'nowrap',
    });

    const speedSlider = document.createElement('input');
    speedSlider.type = 'range';
    speedSlider.id = 'accessify-tts-speed';
    speedSlider.min = '0.5';
    speedSlider.max = '2';
    speedSlider.step = '0.1';
    speedSlider.value = String(options.speed);
    speedSlider.setAttribute('aria-label', lbl.speed);
    Object.assign(speedSlider.style, {
      width: '70px',
      accentColor: '#4ea8de',
      cursor: 'pointer',
    });

    const speedValue = document.createElement('span');
    speedValue.className = 'accessify-tts-speed-val';
    speedValue.textContent = `${options.speed.toFixed(1)}x`;
    Object.assign(speedValue.style, {
      fontSize: '11px',
      minWidth: '30px',
      textAlign: 'center',
      opacity: '0.8',
    });

    speedSlider.addEventListener('input', () => {
      const newSpeed = parseFloat(speedSlider.value);
      options.speed = newSpeed;
      speedValue.textContent = `${newSpeed.toFixed(1)}x`;
      savePreferences();

      // Update rate on any currently queued utterances
      for (const u of currentUtterances) {
        u.rate = newSpeed;
      }
    });

    speedContainer.appendChild(speedLabel);
    speedContainer.appendChild(speedSlider);
    speedContainer.appendChild(speedValue);

    // Separator
    const sep = document.createElement('div');
    Object.assign(sep.style, {
      width: '1px',
      height: '18px',
      background: 'rgba(255,255,255,0.2)',
      margin: '0 4px',
    });

    // Hint text
    const isTouch = navigator.maxTouchPoints > 0;
    const hint = document.createElement('span');
    hint.className = 'accessify-tts-hint';
    hint.textContent = isTouch ? lbl.hintTouch : lbl.hintClick;
    Object.assign(hint.style, {
      fontSize: '11px',
      opacity: '0.6',
      whiteSpace: 'nowrap',
      display: window.innerWidth <= 640 ? 'none' : 'inline',
    });

    // Screen reader note (visually hidden, available to AT)
    const srNote = document.createElement('span');
    Object.assign(srNote.style, {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: '0',
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0,0,0,0)',
      whiteSpace: 'nowrap',
      border: '0',
    });
    srNote.textContent = lbl.srNote;

    bar.appendChild(playPauseBtn);
    bar.appendChild(stopBtn);
    bar.appendChild(sep);
    bar.appendChild(speedContainer);
    bar.appendChild(sep.cloneNode(true));
    bar.appendChild(hint);
    bar.appendChild(srNote);

    return bar;
  }

  function updateControlBarState() {
    if (!controlBar) return;
    const lbl = ttsLabels();

    const playBtn = controlBar.querySelector<HTMLButtonElement>(
      '.accessify-tts-play'
    );
    const hintEl = controlBar.querySelector<HTMLSpanElement>(
      '.accessify-tts-hint'
    );

    if (playBtn) {
      if (isPlaying && !isPaused) {
        playBtn.innerHTML = iconPause();
        playBtn.setAttribute('aria-label', lbl.pause);
        playBtn.title = lbl.pause;
      } else if (isPlaying && isPaused) {
        playBtn.innerHTML = iconPlay();
        playBtn.setAttribute('aria-label', lbl.resume);
        playBtn.title = lbl.resume;
      } else {
        playBtn.innerHTML = iconPlay();
        playBtn.setAttribute('aria-label', lbl.playPause);
        playBtn.title = lbl.playPause;
      }
    }

    if (hintEl) {
      if (isPlaying && !isPaused) {
        hintEl.textContent = lbl.speaking;
      } else if (isPaused) {
        hintEl.textContent = lbl.paused;
      } else {
        hintEl.textContent = lbl.hintClick;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Inline SVG icons for control buttons
  // ---------------------------------------------------------------------------

  function iconPlay(): string {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><polygon points="6,3 20,12 6,21"/></svg>';
  }

  function iconPause(): string {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><rect x="5" y="3" width="4" height="18"/><rect x="15" y="3" width="4" height="18"/></svg>';
  }

  function iconStop(): string {
    return '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden="true"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>';
  }

  // ---------------------------------------------------------------------------
  // Styles (injected into <head>, not Shadow DOM)
  // ---------------------------------------------------------------------------

  function getStyles(): string {
    return `
      /* accessify TTS styles */
      .${HIGHLIGHT_CLASS} {
        background-color: rgba(78, 168, 222, 0.15) !important;
        outline: 2px solid rgba(78, 168, 222, 0.5) !important;
        outline-offset: 2px !important;
        border-radius: 3px !important;
        transition: background-color 0.2s ease, outline-color 0.2s ease !important;
      }

      #${CONTROL_BAR_ID} .accessify-tts-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        margin: 0;
        border: 1px solid rgba(255,255,255,0.15);
        border-radius: 6px;
        background: rgba(255,255,255,0.08);
        color: #f0f0f0;
        cursor: pointer;
        transition: background 0.15s ease, border-color 0.15s ease;
        line-height: 1;
      }

      #${CONTROL_BAR_ID} .accessify-tts-btn:hover {
        background: rgba(255,255,255,0.18);
        border-color: rgba(255,255,255,0.3);
      }

      #${CONTROL_BAR_ID} .accessify-tts-btn:focus-visible {
        outline: 2px solid #4ea8de;
        outline-offset: 1px;
      }

      #${CONTROL_BAR_ID} .accessify-tts-btn:active {
        background: rgba(255,255,255,0.25);
      }

      @media (pointer: coarse) {
        #${CONTROL_BAR_ID} .accessify-tts-btn {
          width: 40px;
          height: 40px;
        }
        #${CONTROL_BAR_ID} .accessify-tts-hint {
          display: none;
        }
      }
    `;
  }

  function injectStyles() {
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = getStyles();
  }

  function removeStyles() {
    const styleEl = document.getElementById(STYLE_ID);
    styleEl?.remove();
  }

  // ---------------------------------------------------------------------------
  // Preferences persistence
  // ---------------------------------------------------------------------------

  function loadPreferences() {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        options.speed = Math.min(2, Math.max(0.5, parsed.speed ?? DEFAULT_OPTIONS.speed));
      } catch {
        options = { ...DEFAULT_OPTIONS };
      }
    }
  }

  function savePreferences() {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ speed: options.speed }));
  }

  // ---------------------------------------------------------------------------
  // Module lifecycle
  // ---------------------------------------------------------------------------

  function activate() {
    if (enabled) return;
    enabled = true;

    loadPreferences();
    detectedLang = detectPageLanguage();

    // Load voices async — don't block activation
    waitForVoices().then(() => refreshVoice());

    // Inject styles and control bar
    injectStyles();
    controlBar = createControlBar();
    document.documentElement.appendChild(controlBar);

    // Enable click-to-read
    clickToReadActive = true;
    document.addEventListener('click', handleClick, true);
  }

  function deactivate() {
    enabled = false;

    // Stop all speech
    stopSpeech();

    // Remove click handler
    document.removeEventListener('click', handleClick, true);
    clickToReadActive = false;

    // Remove control bar
    if (controlBar) {
      controlBar.remove();
      controlBar = null;
    }

    // Remove highlights
    removeHighlight();

    // Remove styles
    removeStyles();

    selectedVoice = null;
  }

  // ---------------------------------------------------------------------------
  // FeatureModule interface
  // ---------------------------------------------------------------------------

  return {
    id: 'tts',
    name: () => 'Text-to-Speech',
    description: 'Click any text to hear it read aloud with adjustable speed',
    icon: 'tts',
    category: 'cognitive',
    activate,
    deactivate,
    getState: (): FeatureState => ({
      id: 'tts',
      enabled,
      value: { speed: options.speed },
    }),
    setState: (newState: Partial<TTSOptions>) => {
      if (newState.speed !== undefined) {
        options.speed = Math.min(2, Math.max(0.5, newState.speed));
        savePreferences();

        // Update the slider in the control bar if present
        const slider = document.getElementById(
          'accessify-tts-speed'
        ) as HTMLInputElement | null;
        if (slider) {
          slider.value = String(options.speed);
        }
        const valDisplay = controlBar?.querySelector<HTMLSpanElement>(
          '.accessify-tts-speed-val'
        );
        if (valDisplay) {
          valDisplay.textContent = `${options.speed.toFixed(1)}x`;
        }
      }
    },
  };
}
